import React, { forwardRef, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Android Chrome opens its photo picker for a plain image file input, and that
// picker has no camera option. On Android (except Samsung Internet, whose own
// chooser already offers the camera) ask first: take a photo or pick one.
// Everywhere else this behaves exactly like the <input type="file"> it replaces.
const needsSourceChoice = () =>
  typeof navigator !== "undefined" &&
  /Android/i.test(navigator.userAgent) &&
  !/SamsungBrowser/i.test(navigator.userAgent);

const ProfilePhotoInput = forwardRef(function ProfilePhotoInput(
  { onChange, ...inputProps },
  ref,
) {
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const openGalleryRef = useRef(false);
  const [choosing, setChoosing] = useState(false);

  const setGalleryRef = (el) => {
    galleryRef.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  };

  const handleGalleryClick = (e) => {
    if (openGalleryRef.current || !needsSourceChoice()) {
      openGalleryRef.current = false;
      return;
    }
    e.preventDefault();
    setChoosing(true);
  };

  // Menu clicks must not reach a wrapping <label> or a modal's backdrop handler.
  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const choose = (source) => (e) => {
    stop(e);
    setChoosing(false);
    if (source === "camera") {
      if (cameraRef.current) cameraRef.current.value = "";
      cameraRef.current?.click();
    } else if (source === "gallery") {
      openGalleryRef.current = true;
      galleryRef.current?.click();
    }
  };

  const optionClass =
    "w-full px-4 py-3 text-left text-base text-gray-800 rounded-lg hover:bg-gray-100";

  return (
    <>
      <input
        {...inputProps}
        ref={setGalleryRef}
        type="file"
        onChange={onChange}
        onClick={handleGalleryClick}
      />
      <input
        ref={cameraRef}
        type="file"
        accept={inputProps.accept || "image/*"}
        capture="user"
        onChange={onChange}
        className="hidden"
        style={{ display: "none" }}
        tabIndex={-1}
        aria-hidden="true"
      />
      {choosing &&
        createPortal(
          <div
            className="fixed inset-0 z-[60000] flex items-end justify-center bg-black/40"
            onClick={choose("cancel")}
          >
            <div
              className="w-full max-w-sm m-4 rounded-xl bg-white p-2 shadow-xl"
              onClick={stop}
            >
              <button type="button" className={optionClass} onClick={choose("camera")}>
                Take photo
              </button>
              <button type="button" className={optionClass} onClick={choose("gallery")}>
                Choose from gallery
              </button>
              <button
                type="button"
                className={`${optionClass} text-gray-500`}
                onClick={choose("cancel")}
              >
                Cancel
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
});

export default ProfilePhotoInput;
